/* eslint-disable complexity, sonarjs/cognitive-complexity, filenames/match-exported, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, react/forbid-component-props */
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useRef, useState } from 'react';
import ReactCookies from 'react-cookies';
import { useDispatch, useSelector } from 'react-redux';
import { COOKIES } from '../../brands/default/constants';
import {
  LINK_CHECKOUT_BUTTON,
  LINK_MY_VOUCHERS,
  LINK_SECOND_CHECKOUT,
} from '../../brands/default/links';
import { trackEvent } from '../../helpers/analytics';
import { basketCreator, mergeProducts } from '../../helpers/basketCreator';
import {
  getIsChooseYourOptionsDeal,
  getIsChooseYourProductDeal,
  useDealImpresssion,
  useIsExpressBuy,
} from '../../helpers/deals';
import errorTrackingService from '../../helpers/errorTrackingService';
import giftsExpressBuyService from '../../helpers/giftsExpressBuyService';
import { redirectToURL } from '../../helpers/url';
import { getIsEligibleForVip } from '../../helpers/vipSubscribeHelper';
import { getCurrencyWallet } from '../../helpers/wallet';
import PurchaseContext from '../../providers/PurchaseProvider';
import ThemeContext from '../../providers/ThemeProvider';
import { createBasket, fetchBasket } from '../../redux/actions/basket';
import { setLoadingState } from '../../redux/actions/loading';
import { LOADING_STATUS } from '../../redux/reducers/loading';
import { replaceBasket } from '../../services/basket';
import WowModal from '../WowModal';
import { logToSplunk } from '../_generic/axiosSplunk/axiosSplunk';
import DealSingleSelectModal from '../deal/shared-components/buy-buttons/DealSingleSelectModal';
import SecondCheckoutChooseYourOptionsModal from '../deal/shared-components/buy-buttons/SecondCheckoutChooseYourOptionsModal';
import SecondCheckoutChooseYourProductModal from '../deal/shared-components/buy-buttons/SecondCheckoutChooseYourProductModal';

const MapViewDealModal = ({
  deal = null,
  isClaim = false,
  isValidExternal,
  position = 0,
  isClicked,
  setIsClicked,
  payDeposit = false,
}) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();
  const router = useRouter();
  const location = useSelector((state) => state.locations.location.shortName);
  const componentRef = useRef(null);
  const isHeadlineBig = deal?.headline?.length > 58;
  useDealImpresssion({ componentRef, dealId: deal?.id, location });
  const isChooseYourOptions = getIsChooseYourOptionsDeal(deal);
  const isChooseYourProduct = getIsChooseYourProductDeal(deal);
  const [finePrintCollapsed, setFinePrintCollapsed] = useState(true);
  const [fullDetailsCollapsed, setFullDetailsCollapsed] = useState(true);
  const [locationCollapsed, setLocationCollapsed] = useState(true);
  const locationStore = useSelector((state) => state.locations);
  const [userWallet, isAuthenticated] = useSelector((state) => [
    state.wallet.userWallet,
    state.user.isAuthenticated,
  ]);
  const [, setOrderId] = useContext(PurchaseContext);
  const isGiftExpressBuyable = useIsExpressBuy(
    deal?.id,
    isAuthenticated,
  )?.giftClaimable;

  const userVipStatus = useSelector(
    (state) => state.user.userprofile.userVipStatus,
  );
  const userBalance =
    getCurrencyWallet(userWallet)?.balanceWithValidExperienceCredit;
  let sumWithInitial;
  if (position?.length > 0) {
    sumWithInitial = position?.reduce(
      (previousValue, currentValue, currentIndex) => {
        if (currentValue > 0) {
          return (
            previousValue + deal.products[currentIndex].price * currentValue
          );
        }

        return previousValue;
      },
      0,
    );
  }
  const isQuickBuy =
    userBalance > sumWithInitial && isClaim && isGiftExpressBuyable;

  const expressBuyFailToCheckout = async () => {
    let products = basketCreator(deal, locationStore);

    const basketToken = ReactCookies.load(COOKIES.basketToken);
    const customerToken = ReactCookies.load(COOKIES.customerToken);
    const currentBasket = await fetchBasket(
      basketToken,
      customerToken,
      isAuthenticated,
    );

    if (currentBasket?.products) {
      // if there were products before, we merge them
      products = mergeProducts(currentBasket.products, products);
    }

    const btAux = basketToken
      ? await replaceBasket(
          basketToken,
          products,
          isAuthenticated,
          getIsEligibleForVip(isAuthenticated, userVipStatus),
        )
      : await createBasket(
          products,
          getIsEligibleForVip(isAuthenticated, userVipStatus),
        );

    if (!btAux && !basketToken) {
      errorTrackingService.logError({
        error: 'No Basket Token',
        message: 'Basket token cannot be undefined.',
      });
    }
    redirectToURL(`${LINK_CHECKOUT_BUTTON}/${btAux || basketToken}`, router);
  };
  const expressBuySuccess = (orderId) => {
    dispatch(setLoadingState(LOADING_STATUS.READY));
    setOrderId(orderId);
    // Redirect users based on the second-checkout cookie
    const secondCheckoutEnabled =
      ReactCookies.load(COOKIES.secondCheckout) === 'b';
    if (secondCheckoutEnabled) {
      redirectToURL(`${LINK_SECOND_CHECKOUT}/benefits/${orderId}`, router);
    } else {
      // existing page if cookie value is 'a', or no cookie
      redirectToURL(LINK_MY_VOUCHERS, router);
    }
  };
  const handleBuy = async () => {
    trackEvent('MV_buy_click');
    if (isValidExternal && isValidExternal() === false) return;

    const basketToken = ReactCookies.load(COOKIES.basketToken);
    const customerToken = ReactCookies.load(COOKIES.customerToken);
    const currentBasket = await fetchBasket(
      basketToken,
      customerToken,
      isAuthenticated,
    );

    let products = basketCreator(
      deal,
      locationStore,
      position,
      false,
      null,
      payDeposit,
    );

    // If deal is express buyable then add quick buy
    if (isQuickBuy) {
      giftsExpressBuyService.failToCheckout = expressBuyFailToCheckout;
      giftsExpressBuyService.success = expressBuySuccess;
      giftsExpressBuyService.setProgressTest = (message) =>
        dispatch(setLoadingState(LOADING_STATUS.LOADING, message));
      giftsExpressBuyService.products = products;
      giftsExpressBuyService.initMultiExpressBuy(deal, products);
    } else {
      if (currentBasket?.products) {
        // if there were products before, we merge them
        products = mergeProducts(currentBasket.products, products);
      }

      const newBasketToken = ReactCookies.load(COOKIES.basketToken);
      if (basketToken !== newBasketToken) {
        logToSplunk({
          bt: basketToken,
          ct: customerToken,
          description: 'Different basketTokens found from DealBuyButton',
          newBt: newBasketToken,
        });
      }
      const btAux =
        newBasketToken && newBasketToken?.length > 10
          ? await replaceBasket(
              newBasketToken,
              products,
              isAuthenticated,
              getIsEligibleForVip(isAuthenticated, userVipStatus),
            )
          : await createBasket(
              products,
              getIsEligibleForVip(isAuthenticated, userVipStatus),
            );
      redirectToURL(
        `${LINK_CHECKOUT_BUTTON}/${btAux || newBasketToken}`,
        router,
      );
    }
  };

  return (
    <>
      {isClicked && isChooseYourOptions && (
        <WowModal
          ariaLabelledBy="choose-your-options"
          centered
          extraClasses="choose-your-options"
          setShowModal={setIsClicked}
          showModal={isClicked}
        >
          <SecondCheckoutChooseYourOptionsModal
            deal={deal}
            finePrintCollapsed={finePrintCollapsed}
            fullDetailsCollapsed={fullDetailsCollapsed}
            handleBuyButtonClick={handleBuy}
            locationCollapsed={locationCollapsed}
            mapView
            setFinePrintCollapsed={setFinePrintCollapsed}
            setFullDetailsCollapsed={setFullDetailsCollapsed}
            setLocationCollapsed={setLocationCollapsed}
          />
        </WowModal>
      )}
      {isClicked && isChooseYourProduct && (
        <WowModal
          ariaLabelledBy="choose-your-options"
          centered
          extraClasses="choose-your-options"
          setShowModal={setIsClicked}
          showModal={isClicked}
        >
          <SecondCheckoutChooseYourProductModal
            deal={deal}
            finePrintCollapsed={finePrintCollapsed}
            fullDetailsCollapsed={fullDetailsCollapsed}
            handleBuyButtonClick={handleBuy}
            locationCollapsed={locationCollapsed}
            mapView
            setFinePrintCollapsed={setFinePrintCollapsed}
            setFullDetailsCollapsed={setFullDetailsCollapsed}
            setLocationCollapsed={setLocationCollapsed}
          />
        </WowModal>
      )}
      {isClicked && !isChooseYourOptions && !isChooseYourProduct && (
        <WowModal
          ariaLabelledBy="single-select-deal"
          centered
          extraClasses="single-select-deal"
          setShowModal={setIsClicked}
          showModal={isClicked}
        >
          <DealSingleSelectModal
            deal={deal}
            finePrintCollapsed={finePrintCollapsed}
            fullDetailsCollapsed={fullDetailsCollapsed}
            handleBuyButtonClick={handleBuy}
            mapView
            setFinePrintCollapsed={setFinePrintCollapsed}
            setFullDetailsCollapsed={setFullDetailsCollapsed}
          />
        </WowModal>
      )}
      <style jsx>{`
        .image-container {
          position: relative;
          display: flex;
        }
        .deal-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-grow: 1;
          width: 100%;
          padding: 0 10px;
          text-decoration: none;
        }
        .deal-title {
          flex-grow: 1;
          z-index: 1000;
          position: fixed;
          cursor: pointer;
          visibility: visible;
          bottom: 28%;
          color: white;
          max-width: 82%;
          margin-left: 5px;
        }
        .headline {
          font-size: 14px;
          font-weight: bold;
          margin: 0;
          color: ${theme.colors.white};
        }
        .postage-text {
          margin-top: ${isHeadlineBig ? '-6px' : '0px'};
        }
        .medium-deal :global(.card) {
          width: 100%;
          height: 100%;
        }
        .medium-deal :global(.primary-button) {
          min-width: 136px;
          width: 96px;
          margin-right: 15px;
        }
        .money-item {
          line-height: 1em;
          margin-bottom: 0;
          min-width: 60px;
          display: flex;
          gap: 0.5rem;
          flex-direction: column;
        }
        .label {
          text-transform: uppercase;
          color: black;
        }
        .value-strike {
          font-size: 16px;
          color: ${theme.colors.primary};
          background: linear-gradient(
            to left top,
            transparent 47.75%,
            currentColor 49.5%,
            currentColor 50.5%,
            transparent 52.25%
          );
        }
        .value {
          font-size: 16px;
          color: ${theme.colors.primary};
        }
        .money-item:last-child {
          margin-right: 0;
        }
        @media (max-width: ${theme.breakpoints.xlDown}) {
          :global(.medium-deal) {
            width: 323px;
            height: 290px;
            margin-bottom: 5px;
          }
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .medium-deal {
            width: 273px;
            height: 256px;
            border-radius: 20px;
          }
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.medium-deal) {
            border-radius: 10px;
            width: 100% !important;
            height: auto !important;
            margin-bottom: 10px;
          }
          .deal-info {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-grow: 1;
            width: 100%;
            padding: 0 10px;
            text-decoration: none;
            margin: 5px;
          }
          .deal-title {
            bottom: 20%;
          }
          .headline {
            color: ${theme.colors.dealmobiletitle};
            font-size: 16px;
            line-height: normal;
            font-weight: bold;
          }
          .medium-deal :global(.card) {
            width: 100%;
            height: auto;
            margin-bottom: 0;
            border-radius: 20px;
          }
          .medium-deal :global(.primary-button) {
            // display: none;
          }
          .medium-deal :global(.bought-count) {
            position: absolute;
            padding: 0;
            margin: 0;
            bottom: 10px;
            background-color: transparent;
            color: white;
          }
        }
      `}</style>
    </>
  );
};
MapViewDealModal.propTypes = {
  deal: PropTypes.object,
  isClaim: PropTypes.bool,
  isClicked: PropTypes.bool,
  isValidExternal: PropTypes.func,
  payDeposit: PropTypes.bool,
  position: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  setIsClicked: PropTypes.func,
};
export default MapViewDealModal;
